import { createFileRoute, SearchSchemaInput, useNavigate, useRouter } from '@tanstack/react-router'
import backIcon from "@/assets/paytm/back.png"
import searchIcon from "@/assets/paytm/search.png"
import dismissIcon from "@/assets/paytm/dismiss.png"
import pinIcon from "@/assets/paytm/map_pin.png"
import selectIcon from "@/assets/paytm/select.png"
import markerIcon from "@/assets/paytm/loc_marker.png"
import mapPinIcon from "@/assets/paytm/map_pin_blue.png"
import curLocIcon from "@/assets/paytm/current_location.png"
import { useEffect, useReducer } from "react"
import { Api, formatAddress, generateRandomString, googleApiKey, splitAddressText } from "@/lib/utils"
import { Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import Button from "@/components/features/paytm/button"
import { LocationAddress, PlaceAutoComplete, PlaceDetails } from "@/lib/interfaces"
import AddLocation from "@/components/features/paytm/add-location"
import { useMutation } from "@tanstack/react-query"
import { toast } from 'sonner'
import { useStore } from '@/store'
import { useShallow } from 'zustand/shallow'

interface State {
    placesResponse: { placeId: string, addrLine1: string, name: string, addrLine2: string }[]
    searchText: string
    selectedAddress: LocationAddress | undefined
    markerPosition: { lat: number, lng: number } | undefined
    curLocAvailable: boolean
    showForm: boolean
    reCenteredMap: boolean
    useLocationName: boolean
}

const initialValue: State = {
    searchText: '', selectedAddress: undefined, markerPosition: undefined, placesResponse: [],
    curLocAvailable: false, showForm: false, reCenteredMap: false, useLocationName: true
}

const reducer = (state: State, action: { type: 'reset', payload: Partial<State> } | { type: 'update', payload: Partial<State> }) => {
    switch (action.type) {
        case "update":
            return { ...state, ...action.payload }
        case "reset":
            return { ...initialValue, ...action.payload }
    }
}


const Place = ({ name, addrLine1, addrLine2, lastItem, onClick }: {
    name: string,
    addrLine1: string,
    addrLine2: string,
    lastItem?: boolean,
    onClick: () => void
}) => {
    return <div className="flex items-center bg-white">
        <div className="px-4">
            <img src={pinIcon} className="w-8" />
        </div>
        <div className={`flex items-center justify-between w-full ${lastItem ? '' : 'border-b'}  py-4 pr-2.5`} onClick={onClick}>
            <div>
                <p className="font-semibold">{name}</p>
                <p className="text-xs mt-1.5">{addrLine1}</p>
                <p className="text-xs mt-1">{addrLine2}</p>
            </div>
            <img src={selectIcon} className="w-8" />
        </div>
    </div>
}

const SearchPlace = ({ onClose, searchText, onChange, clear }: { onClose: () => void, searchText: string, onChange: (val: string) => void, clear: () => void }) => {
    return <div className="absolute top-0 px-4 py-5 flex items-center w-full">
        <img src={backIcon} className="w-6 mr-4" onClick={onClose} />
        <div className="flex items-center border h-10 rounded-md justify-between p-1.5 w-full">
            <div className="flex items-center">
                <img src={searchIcon} className="w-6 mr-2" />
                <input className="h-9 w-64 font-semibold px-1 outline-none border-none" placeholder="Search Location"
                    value={searchText} onChange={e => onChange(e.target.value)} autoFocus />
            </div>
            <img src={dismissIcon} className="w-6" onClick={clear} />
        </div>
    </div>
}



export const Route = createFileRoute('/paytm/neworder/location')({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown> & SearchSchemaInput): {
        selectedAddress: LocationAddress,
        phone: string,
        storeId: string | undefined,
        addressType: "store" | "customer"
    } => {
        return {
            selectedAddress: search.selectedAddress as LocationAddress || undefined,
            phone: search.phone as string || '',
            storeId: search.storeId as string | undefined,
            addressType: search.addressType as "store" | "customer" || 'store'
        }
    }
})

function RouteComponent() {
    const { selectedAddress, storeId, addressType, phone } = Route.useSearch()

    const [state, dispatch] = useReducer(reducer, initialValue)
    const map = useMap()

    const { token } = useStore(useShallow(state => ({ token: state.token })))

    const reverseGeoLookup = useMutation({
        mutationFn: (data: { lat: number, lng: number }) => {
            return new Promise<{ results: { address_components: { long_name: string, short_name: string, types: string[] }[], formatted_address: string, place_id: string }[] }>((resolve, reject) => {
                fetch(`https://maps.googleapis.com/maps/api/geocode/json?result_type=premise&latlng=${data.lat},${data.lng}&key=${googleApiKey}`, {
                    method: 'get'
                })
                    .then(resp => {
                        return resp.json()
                    })
                    .then(respJson => {
                        resolve(respJson)
                    })
                    .catch(err => {
                        console.log(err)
                        reject(err)
                    })
            })
        },
        onSuccess(data, variables) {
            const formattedAddress = formatAddress(data.results[0].formatted_address, data.results[0].address_components.map((e: { long_name: string; types: string[] }) => ({ longText: e.long_name, types: e.types })))
            const premise = data.results[0].address_components.find((e: { long_name: string; types: string[] }) => e.types.includes('premise'))
            dispatch({
                type: 'update', payload: {
                    selectedAddress: {
                        address: { name: premise ? premise.long_name : '', line1: formattedAddress.line1, line2: formattedAddress.line2, city: formattedAddress.city, state: formattedAddress.state },
                        pincode: formattedAddress.pincode, lat: variables.lat, lng: variables.lng, placeId: data.results[0].place_id,
                        phone: state.selectedAddress?.phone || phone || ''
                    },
                    useLocationName: false
                }
            })
        }
    })

    const googlePlacesSearch = useMutation({
        mutationFn: (data: { searchText: string }) => {
            return new Promise<{ placeId: string, name: string, addrLine1: string, addrLine2: string }[]>((resolve, reject) => {
                fetch('https://places.googleapis.com/v1/places:autocomplete', {
                    method: 'post', headers: {
                        'Content-Type': 'application/json',
                        'X-Goog-Api-Key': googleApiKey,
                    },
                    body: JSON.stringify({ input: data.searchText, includedRegionCodes: ["in"] })
                })
                    .then(resp => {
                        return resp.json()
                    })
                    .then(respJson => {
                        resolve((respJson.suggestions || []).map((e: PlaceAutoComplete) => {
                            const [addrLine1, addrLine2] = splitAddressText(e.placePrediction.structuredFormat.secondaryText.text)
                            return {
                                name: e.placePrediction.structuredFormat.mainText.text,
                                placeId: e.placePrediction.placeId,
                                addrLine1: addrLine1,
                                addrLine2: addrLine2,
                            }
                        }))
                    }).catch(err => {
                        console.log(err)
                        reject(err)
                    })
            })
        },
        onSuccess(data) {
            dispatch({ type: 'update', payload: { placesResponse: data } })
        },
    })

    const getPlaceDetails = useMutation({
        mutationFn: (placeId: string) => {
            return new Promise<PlaceDetails>((resolve, reject) => {
                fetch(`https://places.googleapis.com/v1/places/${placeId}`, {
                    method: 'get', headers: {
                        'Content-Type': 'application/json',
                        'X-Goog-Api-Key': googleApiKey,
                        'X-Goog-FieldMask': 'displayName,shortFormattedAddress,id,location,addressComponents'
                    }
                })
                    .then(resp => {
                        return resp.json()
                    })
                    .then(respJson => {
                        resolve(respJson)
                    }).catch(err => {
                        console.log(err)
                        reject(err)
                    })
            })
        },
        onSuccess(data, placeId, _context) {
            const formattedAddress = formatAddress(data.shortFormattedAddress, data.addressComponents)
            map?.setCenter({ lat: data.location.latitude, lng: data.location.longitude })
            dispatch({
                type: 'update', payload: {
                    selectedAddress: {
                        address: {
                            name: data.displayName.text, line1: formattedAddress.line1,
                            line2: formattedAddress.line2, city: formattedAddress.city,
                            state: formattedAddress.state
                        },
                        pincode: formattedAddress.pincode, lat: data.location.latitude, lng: data.location.longitude, placeId,
                        phone: state.selectedAddress?.phone || phone || ''
                    },
                    markerPosition: { lat: data.location.latitude, lng: data.location.longitude },
                    useLocationName: false
                }
            })
        },
    })

    const editOutlet = useMutation({
        mutationFn: (data: { storeId: string | undefined, location: LocationAddress }) => {
            return Api<{ status: number, message: string }>(`/webui/pickup/${storeId ? 'update' : 'create'}`, "POST",
                {
                    store_id: data.storeId,
                    lat: data.location.lat,
                    lng: data.location.lng,
                    address: {
                        name: data.location.address.name,
                        line1: data.location.address.line1,
                        line2: data.location.address.line2,
                        city: data.location.address.city,
                        state: data.location.address.state,
                    },
                    pincode: data.location.pincode,
                    phone: state.selectedAddress?.phone || phone,
                    google_place_id: data.location.placeId
                }, { token })
        },
        onError(error) {
            toast.error(error.message || `Error ${storeId ? 'editing' : 'adding'} outlet`)
        },
        onSuccess(_data, variables) {
            navigate({
                to: '/paytm/neworder', search: {
                    address: variables.location,
                    contact: variables.location.phone || phone,
                    addressType: 'store',
                    storeId: variables.storeId
                }
            })
        }
    })

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.permissions
                .query({ name: "geolocation" })
                .then(function (result) {
                    if (result.state === "granted") {
                        dispatch({ type: 'update', payload: { curLocAvailable: true } })
                    }
                });
        }
        if (selectedAddress) {
            dispatch({ type: 'update', payload: { selectedAddress, showForm: true, markerPosition: { lat: selectedAddress.lat, lng: selectedAddress.lng } } })
        } else {
            dispatch({ type: 'update', payload: { useLocationName: false } })
        }
    }, []);

    const markNewLocation = (loc: google.maps.LatLng | undefined) => {
        const lat = loc?.lat()
        const lng = loc?.lng()
        if (lat && lng) {
            reverseGeoLookup.mutate({ lat, lng })
        }
    }

    const router = useRouter();
    const navigate = useNavigate()

    return <div className="fixed left-0 right-0 top-0 bottom-0 bg-white z-30">
        <SearchPlace onClose={() => router.history.back()} searchText={state.searchText} onChange={val => {
            dispatch({ type: 'update', payload: { searchText: val, selectedAddress: undefined } })
            if (val.length > 2) {
                googlePlacesSearch.mutate({ searchText: val })
            }
        }} clear={() => dispatch({ type: 'update', payload: { searchText: '' } })} />

        <div className="absolute top-20 bottom-0 left-0 right-0 overflow-auto bg-white border-t">
            <Map
                style={{ width: '100%', height: '100%' }}
                gestureHandling={'greedy'}
                disableDefaultUI={true}
                mapId={'DEMO_MAP_ID'}
                defaultZoom={20}
                defaultCenter={state.markerPosition || { lat: 12.96696626358979, lng: 77.58164391270302 }}
                onTilesLoaded={() => {
                    if (selectedAddress && !state.reCenteredMap) {
                        map?.setCenter({ lat: selectedAddress.lat, lng: selectedAddress.lng })
                        dispatch({ type: 'update', payload: { reCenteredMap: true } })
                    }
                }}
                onCenterChanged={e => {
                    if (state.selectedAddress) {
                        const lat = e.detail.center.lat
                        const lng = e.detail.center.lng
                        dispatch({
                            type: 'update', payload: {
                                markerPosition: { lat, lng }
                            }
                        })
                    }
                }}
                onDragend={e => markNewLocation(e.map.getCenter())}
                onZoomChanged={e => markNewLocation(e.map.getCenter())}
            >
                {state.markerPosition ? <AdvancedMarker position={state.markerPosition} title={'Drop location'}>
                    <div className='bg-blue-300 flex justify-center items-center w-10 h-10 rounded-full bg-opacity-60'>
                        <img src={markerIcon} className='w-6' />
                    </div>
                </AdvancedMarker> : null}
                <AdvancedMarker position={state.markerPosition} title={'Drop location'} anchorPoint={["100px", "75px"]}>
                    <div className='flex justify-center items-center bg-black px-4 py-2 rounded-full'>
                        <p className="text-white font-semibold">{addressType === 'store' ? 'Order will be picked here' : 'Order will be delivered here'}</p>
                    </div>
                </AdvancedMarker>
            </Map>
            {state.placesResponse.length > 0 && state.selectedAddress === undefined ? <div className="absolute left-0 right-0 top-0 bottom-0">{state.placesResponse.map((e, i) => {
                return <Place name={e.name} addrLine1={e.addrLine1} addrLine2={e.addrLine2}
                    key={e.placeId} lastItem={i === (state.placesResponse.length - 1)} onClick={() => {
                        getPlaceDetails.mutate(e.placeId)
                    }} />
            })}</div> : null}
            {state.selectedAddress ? <div className="bg-white absolute bottom-0 left-0 right-0 h-48 rounded-tl-xl rounded-tr-xl p-4 border-t">
                <div className="flex items-start">
                    <img src={mapPinIcon} className="w-8" />
                    <div className="pt-1 pb-4 px-4 w-full">
                        <textarea className="font-semibold resize-none outline-none border-none text-lg h-8" value={state.selectedAddress.address.name} readOnly />
                        <textarea readOnly className="text-sm resize-none outline-none border-none w-full" value={state.selectedAddress.address.line1 + ', ' + state.selectedAddress.address.line2} />
                    </div>
                </div>
                <Button title="Use this location" onClick={() => {
                    if (!state.useLocationName && state.selectedAddress) {
                        dispatch({ type: 'update', payload: { showForm: true, selectedAddress: { ...state.selectedAddress, address: { ...state.selectedAddress.address, name: '' } } } })
                    } else {
                        dispatch({ type: 'update', payload: { showForm: true } })
                    }
                }} />
            </div> : null}
            {state.curLocAvailable ?
                <div className="absolute bottom-56 w-full flex justify-center items-center">
                    <div className="flex justify-center items-center px-2 pr-3 py-2 bg-white border-2 border-process-cyan rounded-full active:opacity-40" onClick={() => {
                        navigator.geolocation.getCurrentPosition((pos) => {
                            dispatch({ type: 'update', payload: { markerPosition: { lat: pos.coords.latitude, lng: pos.coords.longitude } } })
                            map?.setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude })
                        }, () => null, { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 });
                    }}>
                        <img src={curLocIcon} className="w-6 mr-2" />
                        <p className="text-process-cyan font-semibold">Use current location</p>
                    </div>
                </div> : null}
        </div>
        {state.showForm && state.selectedAddress ? <AddLocation locationDetails={state.selectedAddress}
            onClose={() => router.history.back()} onUpdate={val => state.selectedAddress && dispatch({ type: 'update', payload: { selectedAddress: { ...state.selectedAddress, ...val } } })} onSave={() => {
                if (state.selectedAddress && addressType === 'store') {
                    editOutlet.mutate({ storeId: storeId || generateRandomString(), location: state.selectedAddress })
                } else if (state.selectedAddress && addressType === 'customer') {
                    navigate({
                        to: '/paytm/neworder', search: {
                            address: state.selectedAddress,
                            contact: state.selectedAddress.phone || phone,
                            addressType: 'customer'
                        }
                    })
                }
            }} closeForm={() => dispatch({ type: 'update', payload: { showForm: false } })} mobileNumReadOnly={addressType === 'store' && storeId !== undefined} /> : null}
    </div>

}
